import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { findIdInUrl, getValidId, idKeys, type WithId } from '@restDataAccess/withId';
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';
import { useSiteContext } from '@/providers';

import { authorizationHeader } from '../../authorizationHeader.ts';
import { assertApiBaseForFetch } from '../getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from '../useGetApiUrl.ts';

type AuthApiGetSingleFetch = {
  baseApiRoute: ApiNameType;
  route: string;
  cacheKeys: [string | number, ...unknown[]];
  headers?: HeadersInit;
  rawId: string | number;
  stringResult?: boolean;
};

/**
 * Authenticated GET by ID with TanStack Query.
 *
 * Fetches a single resource from an API route that contains an id placeholder
 * (e.g. `{id}`, `{inventoryId}`, or `{ipAddress}`). The placeholder in the route
 * is replaced by the value of `rawId`, and the response is cached under `cacheKeys`.
 *
 * @typeParam T - The JSON response type.
 *
 * @param options.rawId - The id value to substitute into the route (e.g. inventory id, device id).
 *   Replaces the placeholder found in `route` when building the request URL.
 * @param options.baseApiRoute - API name (e.g. `API_NAMES.inventoryApi`) used to resolve the base URL.
 * @param options.route - Path template with at most one placeholder. Must use a key from {@link idKeys}
 *   (e.g. `/api/v1/name/{id}` or `/api/v1/inventory/{inventoryId}`). The placeholder is detected via
 *   {@link findIdInUrl} and replaced by `rawId`.
 * @param options.cacheKeys - Tuple used as the React Query key prefix. Should include values that uniquely
 *   identify this request (e.g. `[id, CACHE_KEYS.inventoryName]`). `currentSite?.id` is appended automatically
 *   so cache entries do not bleed across sites.
 * @param options.headers - Optional extra request headers (auth is added automatically).
 *
 * @returns The result of `useQuery<T>` plus `invalidateQuery()` to invalidate this query's cache.
 *
 * @example
 * // Route: "/api/v1/name/{id}" → GET /api/v1/name/abc-123
 * const { data, isPending, error, invalidateQuery } = useAuthApiGetSingleFetchById<string>({
 *   baseApiRoute: API_NAMES.inventoryApi,
 *   route: InventoryEndpoints.getNameById,
 *   cacheKeys: [id, CACHE_KEYS.inventoryName],
 *   rawId: id,
 * });
 */
export const useAuthApiGetSingleFetchById = <T>({
  baseApiRoute,
  route,
  cacheKeys,
  headers,
  rawId,
  stringResult = false,
}: AuthApiGetSingleFetch): UseQueryResult<T> & { invalidateQuery: () => void } => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const queryClient = useQueryClient();
  const { currentSite } = useSiteContext();

  const queryKey = [...cacheKeys, currentSite?.id];

  const getData = async (anId: WithId | undefined): Promise<T> => {
    try {
      const base = assertApiBaseForFetch(api);
      // What happens?
      // Get may or may not have an id in the url like this /api/v1/thing/{id}
      // The id may as well be named inventoryId or something else
      // The id is resolved from the WithId object so this is a constraint, the only one, that must be maintained
      // This is why anId may be undefined. Then we don't need to replace anything an only fetch the route
      const id = anId ? getValidId(anId) : undefined;
      const idNameToReplace = findIdInUrl(route);
      const fetchUrl =
        id && idNameToReplace
          ? `${base}${route.replace(`{${idNameToReplace}}`, id)}`
          : `${base}${route}`;
      const response = await fetch(fetchUrl, {
        headers: {
          ...authorizationHeader(auth.user),
          ...(stringResult ? { accept: 'text/plain' } : {}),
          ...headers,
        },
      });

      return await handleResponseErrors<T>(response, id, !stringResult, stringResult);
    } catch (error) {
      console.error(`Failed to fetch`, error);
      throw error;
    }
  };

  const toWithId = <K extends (typeof idKeys)[number]>(
    key: K,
    value: string | number | undefined
  ): WithId | undefined => (value == null ? undefined : ({ [key]: String(value) } as WithId));

  const queryResult = useQuery<T>({
    queryKey,
    enabled: api != null,
    queryFn: () => {
      const idKey = findIdInUrl(route) as (typeof idKeys)[number];
      const idObj = toWithId(idKey, rawId);
      return getData(idObj);
    },
  });

  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  return { ...queryResult, invalidateQuery };
};
